import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { Row, Col, Radio, DatePicker } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Animated } from 'react-animated-css';

import StationSelector from '../../components/dashboard/StationSelector';
import UsageChart from '../../components/analysis/UsageChart';
import YieldChart from '../../components/analysis/YieldChart';
import { YieldIcon, UsageIcon, UtilizationIcon } from '../../components/analysis/AnalysisIcons';

import './Analysis.scss';
import InvertorsPower from '../../components/analytics/invertorspower';
import UtilizationChart from '../../components/analysis/UtilizationChart';
import { initializeStation, formatSummary } from '../../utils/initStation';
import {
  getUserStationList,
  inspectionTimeUpdate,
  getUsageBySiteDate
} from '../../modules/analytics';

class Analysis extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      usageLoading: false,
      activeTab: 'yield', // 'yield' | 'usage' | 'utilization'
    };
  }

  async componentDidMount() {
    this.setState({ loading: true });
    await initializeStation(this.props);
    await this.fetchUsage();
    this.setState({ loading: false });
  }

  async componentDidUpdate(prevProps) {
    const prevStation = prevProps.inspectionTime.stationId;
    const prevDate = prevProps.inspectionTime.date;
    const { stationId, date } = this.props.inspectionTime || {};
    if (prevStation !== stationId || prevDate !== date) {
      await this.fetchUsage();
    }
  }

  fetchUsage = async () => {
    const { inspectionTime, getUsageBySiteDate } = this.props;
    if (!inspectionTime?.stationId) return;
    const date = inspectionTime.date || new Date().toISOString();
    this.setState({ usageLoading: true });
    try {
      // Filter to grid meters to match市电用电量 (grid usage) expectations
      await getUsageBySiteDate(inspectionTime.stationId, date, true, 'grid_electric_meter');
    } catch (_) {
      // ignore; UI will show empty state
    } finally {
      this.setState({ usageLoading: false });
    }
  }

  handleDateChange = (nextDate) => {
    const { inspectionTime, inspectionTimeUpdate } = this.props;
    const picked = nextDate || dayjs();
    if (!picked || !picked.isValid || !picked.isValid()) return;
    const iso = picked.format ? picked.format('YYYY-MM-DD') : picked;
    inspectionTimeUpdate({
      ...inspectionTime,
      date: iso,
    });
  }

  moveDay = (delta) => {
    const { inspectionTime } = this.props;
    const current = inspectionTime.date ? dayjs(inspectionTime.date) : dayjs();
    this.handleDateChange(current.add(delta, 'day'));
  }

  handleTabChange = (e) => {
    this.setState({ activeTab: e.target.value });
  }

  render() {
    const { inspectionTime, usageBySiteDate, stationSummary = {}, inverterData = [] } = this.props;
    const { activeTab } = this.state;
    const stationId = inspectionTime.stationId;
    const dateKey = inspectionTime.date
      ? dayjs(inspectionTime.date).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD');
    const usageData =
      (stationId && dateKey && usageBySiteDate[stationId] && usageBySiteDate[stationId][dateKey]) ||
      {};

    const dateMoment = inspectionTime.date && dayjs(inspectionTime.date).isValid()
      ? dayjs(inspectionTime.date)
      : dayjs();

    // Calculate Yield Stats
    const summary = stationSummary[stationId] || {};
    const stats = formatSummary(summary, inspectionTime);
    const siteInfo = inspectionTime.stationInfo || {};

    const totalGeneration = Array.isArray(inverterData) ? inverterData.reduce((sum, point) => {
      const energyValue = point && (point.energyKWh ?? point.energy ?? point.energy_kWh ?? point.energy_kwH);
      const value = Number(energyValue);
      return Number.isNaN(value) ? sum : sum + value;
    }, 0) : 0;

    const roundedGeneration = Number(totalGeneration.toFixed(3));
    const energyPrice = Number(siteInfo.estimatedSolarEnergyUnitPrice) || 0;
    const earningsValue = Number((totalGeneration * energyPrice).toFixed(2));
    // Use daily change for full load hours if available, similar to Dashboard
    const fullLoadHours = siteInfo.efficiency?.value === undefined ? 0 : (stats.daily?.change || 0);

    return (
      <Animated animationIn="fadeIn">
        <div className="analysisWrapper" style={{ paddingBottom: this.props.play ? '100px' : '20px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} className="leftPanel">
              <StationSelector pageTitle="Analysis" />

              <div className="chartWrapper">
                <div className="controls">
                  <Radio.Group
                    value={activeTab}
                    onChange={this.handleTabChange}
                  >
                    <Radio.Button value="yield"><YieldIcon />Yield</Radio.Button>
                    <Radio.Button value="utilization"><UtilizationIcon />Utilization</Radio.Button>
                    <Radio.Button value="usage"><UsageIcon />Usage</Radio.Button>
                  </Radio.Group>

                  <div className="dateNavigator">
                    <LeftOutlined
                      className="navigatorIcon"
                      onClick={() => this.moveDay(-1)}
                    />
                    <DatePicker
                      allowClear={false}
                      value={dateMoment}
                      onChange={this.handleDateChange}
                      bordered={false}
                      suffixIcon={null}
                      inputReadOnly={true}
                      style={{ width: 110, textAlign: 'center', cursor: 'pointer', padding: 0 }}
                      className="centeredDatePicker"
                    />
                    <RightOutlined
                      className="navigatorIcon"
                      onClick={() => this.moveDay(1)}
                    />
                  </div>
                  {/* Note: The original SiteChart had a DatePicker visible. 
                      If we want to mimic it exactly, we should show it. 
                      However, the design often puts it next to the arrows. 
                      Let's put it next to the arrows if needed, or just use the arrows + text.
                      SiteChart.js had arrows + text AND a picker? No, SiteChart had arrows + text.
                      UsageChart had arrows + text + picker.
                      Let's stick to arrows + text for now as per SiteChart reference.
                   */}
                </div>

                {activeTab === 'yield' && (
                  <YieldChart
                    roundedGeneration={roundedGeneration}
                    earningsValue={earningsValue}
                    fullLoadHours={fullLoadHours}
                  />
                )}
                {activeTab === 'usage' && (
                  <UsageChart
                    loading={this.state.usageLoading}
                    series={usageData.series || []}
                    totals={usageData.totals || {}}
                  />
                )}
                {activeTab === 'utilization' && (
                  <UtilizationChart
                    loading={this.state.usageLoading}
                    series={usageData.series || []}
                    totals={usageData.totals || {}}
                  />
                )}
              </div>

            </Col>
          </Row>
        </div>
      </Animated >
    );
  }
}

const mapStateToProps = (state) => ({
  router: state.router,
  userProfile: state.user.userProfile,
  userStationList: state.analytics.userStationList,
  inspectionTime: state.analytics.inspectionTime,
  usageBySiteDate: state.analytics.usageBySiteDate || {},
  stationSummary: state.analytics.stationSummary,
  inverterData: state.analytics.inverterData,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getUserStationList,
      inspectionTimeUpdate,
      getUsageBySiteDate,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Analysis);
